// Módulos del ERP: fuente común del menú lateral (Sidebar.js) y de la lista
// de permisos por módulo de la ficha del empleado (FichaEmpleado.js).
// Si se añade una entrada al menú, aparece sola en la ficha: así las dos
// listas no se desfasan. El `moduleId` debe coincidir con `routePermissions`
// de App.js.
import {
  LayoutDashboard,
  Users,
  FileText,
  UtensilsCrossed,
  Warehouse,
  DollarSign,
  Calendar,
  Clock,
  Bot,
  Home,
  Settings,
  UserCheck,
  FileCheck,
  Mail,
  TrendingUp,
  Activity,
  Tag,
} from 'lucide-react';

export const MENU_ERP = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['direccion', 'admin'], moduleId: 'dashboard' },
  { icon: Home, label: 'Mi Inicio', path: '/dashboard-empleado', roles: ['all'], moduleId: 'dashboard-empleado', alwaysShow: true },
  { icon: Users, label: 'CRM', path: '/clientes', roles: ['direccion', 'comercial'], moduleId: 'clientes' },
  { icon: FileText, label: 'Presupuestos', path: '/presupuestos', roles: ['direccion', 'comercial'], moduleId: 'presupuestos' },
  { icon: Calendar, label: 'Calendario', path: '/calendario-eventos', roles: ['all'], moduleId: 'calendario-eventos' },
  { icon: Clock, label: 'Fichaje', path: '/fichaje', roles: ['all'], moduleId: 'fichaje' },
  {
    icon: UtensilsCrossed,
    label: 'Catálogo',
    moduleId: 'catalogo',
    roles: ['direccion', 'operaciones'],
    children: [
      { label: 'Menús', path: '/menus', moduleId: 'menus', roles: ['direccion', 'operaciones'] },
      { label: 'Platos', path: '/platos', moduleId: 'platos', roles: ['direccion', 'operaciones'] },
      { label: 'Ingredientes', path: '/ingredientes', moduleId: 'ingredientes', roles: ['direccion', 'operaciones'] },
      { label: 'Materiales', path: '/materiales', moduleId: 'materiales', roles: ['direccion', 'operaciones'] },
      { label: 'Proveedores', path: '/proveedores', moduleId: 'proveedores', roles: ['direccion', 'operaciones'] },
    ],
  },
  {
    icon: Warehouse,
    label: 'Operaciones',
    moduleId: 'operaciones-grupo',
    roles: ['direccion', 'operaciones'],
    children: [
      { label: 'Compras y Almacén', path: '/compras/necesidades', moduleId: 'compras', roles: ['direccion', 'operaciones'] },
      { label: 'Stock y Lotes', path: '/stock', moduleId: 'stock', roles: ['direccion', 'operaciones'] },
      { label: 'Material Asignado', path: '/materiales-asignados', moduleId: 'materiales-asignados', roles: ['direccion', 'operaciones'] },
      { label: 'Furgonetas', path: '/vehiculos', moduleId: 'vehiculos', roles: ['direccion', 'operaciones'] },
    ],
  },
  { icon: UtensilsCrossed, label: 'Tablet Cocina', path: '/tablet-cocina', roles: ['all'], moduleId: 'tablet-cocina' },
  { icon: Tag, label: 'Etiquetas', path: '/etiquetas', roles: ['all'], moduleId: 'etiquetas', alwaysShow: true },
  { icon: Users, label: 'Empleados', path: '/empleados', roles: ['direccion', 'rrhh'], moduleId: 'empleados' },
  { icon: UserCheck, label: 'Gestión Equipo', path: '/gestion-equipo', roles: ['direccion', 'encargado'], moduleId: 'gestion-equipo' },
  { icon: FileCheck, label: 'Gestión Documental', path: '/gestion-documental', roles: ['direccion', 'rrhh'], moduleId: 'gestion-documental' },
  // Desactivado temporalmente hasta implantar el escaneo de facturas (la ruta /subida-facturas sigue activa)
  // { icon: Receipt, label: 'Subida Facturas', path: '/subida-facturas', roles: ['direccion', 'rrhh', 'operaciones', 'comercial', 'encargado'], moduleId: 'subida-facturas' },
  { icon: DollarSign, label: 'Finanzas', path: '/finanzas', roles: ['direccion', 'finanzas'], moduleId: 'finanzas' },
  { icon: DollarSign, label: 'Control de Cobros', path: '/control-cobros', roles: ['direccion', 'finanzas'], moduleId: 'control-cobros' },
  { icon: Mail, label: 'Emails', path: '/emails', roles: ['direccion', 'comercial'], moduleId: 'emails' },
  {
    icon: TrendingUp,
    label: 'Marketing',
    moduleId: 'marketing',
    roles: ['direccion'],
    children: [
      { label: 'Blog', path: '/marketing/blog', moduleId: 'marketing-blog', roles: ['direccion'] },
      { label: 'Web', path: '/marketing/web', moduleId: 'marketing-web', roles: ['direccion'] },
      { label: 'Instagram', path: '/marketing/instagram', moduleId: 'marketing-instagram', roles: ['direccion'] },
      { label: 'Google Business', path: '/marketing/google-business', moduleId: 'marketing-google-business', roles: ['direccion'] },
    ],
  },
  { icon: Bot, label: 'Asistente IA', path: '/asistente', roles: ['direccion'], moduleId: 'asistente' },
  { icon: Settings, label: 'Ajustes', path: '/ajustes', roles: ['direccion'], moduleId: 'ajustes' },
  { icon: Activity, label: 'Uso del ERP', path: '/uso-erp', roles: ['direccion'], moduleId: 'uso-erp' },
];

// Módulos con ruta que no están como entrada del menú (Subida de facturas
// sigue con ruta aunque su entrada del menú esté oculta). Mi Portal se retiró
// (lote C): todo está en Mi Inicio y /portal-empleado redirige allí.
const MODULOS_FUERA_DEL_MENU = [
  { id: 'subida-facturas', label: 'Subida Facturas', grupo: 'General' },
];

// Lista plana de módulos asignables por empleado: [{ id, label, grupo }].
// Las entradas sueltas del menú van al grupo "General"; las de un grupo
// (Catálogo, Operaciones, Marketing) llevan el nombre del grupo.
export const MODULOS_ASIGNABLES = (() => {
  const lista = [];
  MENU_ERP.forEach((item) => {
    if (item.children) {
      item.children.forEach((hijo) => lista.push({ id: hijo.moduleId, label: hijo.label, grupo: item.label }));
    } else if (item.moduleId) {
      lista.push({ id: item.moduleId, label: item.label, grupo: 'General' });
    }
  });
  MODULOS_FUERA_DEL_MENU.forEach((m) => {
    if (!lista.some((x) => x.id === m.id)) lista.push(m);
  });
  return lista;
})();

// Grupos en el orden en que aparecen en el menú: [{ grupo, modulos }]
export const MODULOS_POR_GRUPO = MODULOS_ASIGNABLES.reduce((grupos, modulo) => {
  const existente = grupos.find((g) => g.grupo === modulo.grupo);
  if (existente) existente.modulos.push(modulo);
  else grupos.push({ grupo: modulo.grupo, modulos: [modulo] });
  return grupos;
}, []);
